import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const IMAGES = Array.from({ length: 18 }, (_, i) => `https://singmylove.com/temp/${i + 1}.jpg`);
const MUSIC_URL = 'https://singmylove.com/uploads/1.mp3';

const LOVE_QUOTES = [
  { quote: "In all the world, there is no heart for me like yours.", author: "Maya Angelou" },
  { quote: "I have waited for this opportunity for more than half a century, to repeat to you once again my vow of eternal fidelity and everlasting love.", author: "Gabriel Garcia Marquez" },
  { quote: "Whatever our souls are made of, his and mine are the same.", author: "Emily Bronte" },
  { quote: "I loved her against reason, against promise, against peace, against hope, against happiness, against all discouragement that could be.", author: "Charles Dickens" },
  { quote: "You are my today and all of my tomorrows.", author: "Leo Christopher" },
  { quote: "I wish I could turn back the clock. I'd find you sooner and love you longer.", author: "Unknown" },
  { quote: "Grow old along with me, the best is yet to be.", author: "Robert Browning" },
  { quote: "I swear I couldn't love you more than I do right now, and yet I know I will tomorrow.", author: "Leo Christopher" },
];

const TIMELINE_EVENTS = [
  { year: "2019", title: "Where It All Began", description: "Two souls found each other, and the universe smiled. The moment I saw you, I knew my life was about to change forever." },
  { year: "2020", title: "Through Every Storm", description: "When the world stood still, our love only grew stronger. You became my home, my safe place, my everything." },
  { year: "2021", title: "Growing Together", description: "Every day with you was a new adventure. We laughed, we dreamed, we built a love that could weather any storm." },
  { year: "2022", title: "Deeper in Love", description: "I fell in love with you all over again — with your kindness, your smile, the way you make ordinary moments extraordinary." },
  { year: "2023", title: "Inseparable", description: "By now, I couldn't imagine a single day without you. You are woven into every part of my life, my heart, my soul." },
  { year: "2024", title: "Our Promise", description: "Seven years of love, laughter, and a million beautiful memories. And then came the question that changed everything..." },
  { year: "2025", title: "She Said Yes!", description: "The happiest moment of my life — you said yes! Every year of loving you led to this perfect moment." },
  { year: "Mar 2026", title: "Forever Begins", description: "Next month, we become one. After 7 beautiful years, we're finally getting married. This is just the beginning of our forever." },
];

const PERSONAL_MESSAGES = [
  "Megha, you are the reason I believe in forever.",
  "7 years ago, I found my soulmate. Next month, I marry her.",
  "Every love story is beautiful, but ours is my favourite.",
  "You're not just the love of my life — you're my life itself.",
  "Thank you for choosing me, every single day, for 7 beautiful years.",
  "I promise to love you more with each passing year, starting with forever.",
];

function FloatingHearts() {
  const hearts = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 8,
    duration: 6 + Math.random() * 8,
    size: 10 + Math.random() * 25,
    opacity: 0.1 + Math.random() * 0.4,
  }));

  return (
    <div className="floating-hearts">
      {hearts.map(h => (
        <div
          key={h.id}
          className="heart"
          style={{
            left: `${h.left}%`,
            animationDelay: `${h.delay}s`,
            animationDuration: `${h.duration}s`,
            fontSize: `${h.size}px`,
            opacity: h.opacity,
          }}
        >
          &#x2665;
        </div>
      ))}
    </div>
  );
}

function SparkleField() {
  const sparkles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 2 + Math.random() * 3,
    size: 8 + Math.random() * 16,
  }));

  return (
    <div className="sparkle-field">
      {sparkles.map(s => (
        <div
          key={s.id}
          className="sparkle"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
            fontSize: `${s.size}px`,
          }}
        >
          &#x2726;
        </div>
      ))}
    </div>
  );
}

function useScrollReveal() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return [ref, isVisible];
}

function ScrollReveal({ children, className = '', delay = 0, direction = 'up' }) {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${direction} ${isVisible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function WeddingCountdown() {
  const [timeLeft, setTimeLeft] = useState({});
  const weddingDate = useRef(new Date('2026-03-10T00:00:00'));

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const diff = weddingDate.current - now;
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="countdown-container">
      <h3 className="countdown-label">Until We Say "I Do"</h3>
      <div className="countdown-boxes">
        {Object.entries(timeLeft).map(([unit, value]) => (
          <div key={unit} className="countdown-box">
            <span className="countdown-number">{String(value).padStart(2, '0')}</span>
            <span className="countdown-unit">{unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MusicPlayer() {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);

  const togglePlay = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
      setShowPrompt(false);
    }
  }, [isPlaying]);

  return (
    <>
      <audio ref={audioRef} src={MUSIC_URL} loop preload="auto" />
      {showPrompt && (
        <div className="music-prompt" onClick={togglePlay}>
          <div className="music-prompt-content">
            <div className="music-prompt-heart">&#x1F495;</div>
            <p>Tap to play our song</p>
            <div className="music-prompt-subtitle">& begin our journey</div>
          </div>
        </div>
      )}
      <button className={`music-toggle ${isPlaying ? 'playing' : ''}`} onClick={togglePlay} aria-label="Toggle music">
        <div className="music-bars">
          <span></span><span></span><span></span><span></span>
        </div>
      </button>
    </>
  );
}

function HeroSection() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="hero">
      <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.3}px)` }}>
        <img src={IMAGES[0]} alt="Us" className="hero-image" />
      </div>
      <div className="hero-overlay" />
      <FloatingHearts />
      <div className="hero-content" style={{ transform: `translateY(${scrollY * 0.15}px)`, opacity: Math.max(0, 1 - scrollY / 600) }}>
        <div className="hero-subtitle-top">Happy Valentine's Day</div>
        <h1 className="hero-title">
          <span className="hero-name">Megha</span>
          <span className="hero-ampersand">&</span>
          <span className="hero-name">Gaurav</span>
        </h1>
        <div className="hero-tagline">7 Years of Love. A Lifetime to Go.</div>
        <div className="hero-date">Est. 2019 &#x2022; Getting Married March 2026</div>
        <div className="scroll-indicator">
          <span>Scroll to explore our love story</span>
          <div className="scroll-arrow">&#x2193;</div>
        </div>
      </div>
    </section>
  );
}

function LoveLetterSection() {
  return (
    <section className="section love-letter-section">
      <SparkleField />
      <ScrollReveal>
        <div className="love-letter">
          <div className="letter-decoration">&#x2665;</div>
          <h2 className="section-title script">My Dearest Megha,</h2>
          <div className="letter-body">
            <p>
              Seven years ago, you walked into my life and painted it with colors I never knew existed.
              Every moment with you has been a gift &mdash; every laugh, every whisper, every quiet evening,
              every adventure.
            </p>
            <p>
              You are my first thought in the morning and my last wish at night. You are the calm
              in my chaos, the answer to every prayer I've ever whispered. In a world of billions,
              my heart chose you &mdash; and it would choose you again, in every lifetime, in every universe.
            </p>
            <p>
              Next month, I get to stand before the world and call you my wife. But you've been
              the love of my life since the very first day. Thank you for 7 incredible years.
              Thank you for saying yes. Thank you for being <em>you</em>.
            </p>
            <p className="letter-signature">
              Forever & Always Yours,<br />
              <span className="signature">Gaurav</span>
            </p>
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
}

function QuotesSection() {
  return (
    <section className="section quotes-section">
      <ScrollReveal>
        <h2 className="section-title">Words That Remind Me of Us</h2>
      </ScrollReveal>
      <div className="quotes-grid">
        {LOVE_QUOTES.map((q, i) => (
          <ScrollReveal key={i} delay={i * 100} direction={i % 2 === 0 ? 'left' : 'right'}>
            <div className="quote-card">
              <div className="quote-mark">&ldquo;</div>
              <p className="quote-text">{q.quote}</p>
              <span className="quote-author">&mdash; {q.author}</span>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>
  );
}

function TimelineSection() {
  return (
    <section className="section timeline-section">
      <ScrollReveal>
        <h2 className="section-title">Our Love Story</h2>
        <p className="section-subtitle">Every chapter more beautiful than the last</p>
      </ScrollReveal>
      <div className="timeline">
        <div className="timeline-line" />
        {TIMELINE_EVENTS.map((event, i) => (
          <ScrollReveal key={i} delay={i * 80} direction={i % 2 === 0 ? 'left' : 'right'}>
            <div className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}>
              <div className="timeline-dot">&#x2665;</div>
              <div className="timeline-content">
                <div className="timeline-year">{event.year}</div>
                <h3 className="timeline-title">{event.title}</h3>
                <p className="timeline-desc">{event.description}</p>
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>
  );
}

function GallerySection() {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="section gallery-section">
      <ScrollReveal>
        <h2 className="section-title">Our Beautiful Moments</h2>
        <p className="section-subtitle">Every picture tells a piece of our story</p>
      </ScrollReveal>
      <div className="gallery-grid">
        {IMAGES.map((img, i) => (
          <ScrollReveal key={i} delay={i * 80}>
            <div
              className={`gallery-item item-${i + 1}`}
              onClick={() => setSelectedImage(img)}
            >
              <img src={img} alt={`Our moment ${i + 1}`} loading="lazy" />
              <div className="gallery-overlay">
                <span>&#x2665;</span>
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>

      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={selectedImage} alt="Our moment" />
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</button>
          </div>
        </div>
      )}
    </section>
  );
}

function PersonalMessagesSection() {
  return (
    <section className="section messages-section">
      <SparkleField />
      <ScrollReveal>
        <h2 className="section-title script">Things I Want You to Know</h2>
      </ScrollReveal>
      <div className="messages-container">
        {PERSONAL_MESSAGES.map((msg, i) => (
          <ScrollReveal key={i} delay={i * 150}>
            <div className="message-bubble">
              <span className="message-heart">&#x2665;</span>
              <p>{msg}</p>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>
  );
}

function CountdownSection() {
  return (
    <section className="section countdown-section">
      <div className="countdown-bg">
        <img src={IMAGES[9]} alt="Us" />
      </div>
      <div className="countdown-overlay" />
      <ScrollReveal>
        <h2 className="section-title light">The Countdown Has Begun</h2>
        <p className="section-subtitle light">To the most beautiful day of our lives</p>
        <WeddingCountdown />
      </ScrollReveal>
    </section>
  );
}

function FinalSection() {
  return (
    <section className="section final-section">
      <FloatingHearts />
      <SparkleField />
      <ScrollReveal>
        <div className="final-content">
          <div className="final-ring">&#x1F48D;</div>
          <h2 className="final-title script">Happy Valentine's Day, Megha</h2>
          <p className="final-message">
            You are my forever, my always, my everything.<br />
            I can't wait to marry you.
          </p>
          <div className="final-heart-beat">&#x2665;</div>
          <p className="final-footer">
            With all my love, today and every day<br />
            <span className="script" style={{ fontSize: '2rem' }}>Gaurav</span>
          </p>
        </div>
      </ScrollReveal>
    </section>
  );
}

function App() {
  return (
    <div className="app">
      <MusicPlayer />
      <HeroSection />
      <LoveLetterSection />
      <GallerySection />
      <TimelineSection />
      <QuotesSection />
      <PersonalMessagesSection />
      <CountdownSection />
      <FinalSection />
    </div>
  );
}

export default App;
